import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { PerSubscriptionUserStorage } from '@/utils/storage';
import { parseSegmentParams } from '@/utils/segments';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import { transformAggAppIdIntoBareAppId } from '@/apps/apps.utils';
import { formatDates, createTimeSeriesFromDate } from '@/common/time-series-picker/time-series-picker.util.js';
import { AppChooserService } from '@/common/app-chooser/app-chooser.service.js';
import { AppsService } from '@/common/apps/apps.service';
import { VanillaDateRange } from '@/data/vanilla-date-range';
import { APP_PLATFORM_LIST } from '@/utils/apps';
import {
    getDefaultSelectedApp,
    getDefaultSelectedChannel,
    defaultSelectedFeatureType,
    defaultSelectedAccount,
} from './default-filters.utils';

const isProduction = process.env.NODE_ENV === 'production';

function warn (warning) {
    if (!isProduction) {
        // eslint-disable-next-line no-console
        console.warn(warning);
    }
}

const ACCOUNT_STORAGE_KEY = 'SELECTED_ACCOUNT_KEY';
const APP_STORAGE_KEY = 'app-chooser--storage-key';
const APPS_STORAGE_KEY = 'multi-app-chooser--storage-key';
const CHANNEL_STORAGE_KEY = 'SELECTED_CHANNEL_KEY';
const ALL_TIME_STORAGE_KEY = 'ALL_TIME_STORAGE_KEY';
const FEATURE_TYPE_KEY = 'FEATURE_TYPE_KEY';
const VERIFICATION_KEY = 'VERIFICATION_KEY';
const DEFAULT_DATE = { type: 'Last 30 Days' };
const DEFAULT_DATE_NEW = { type: 'Today' };
const GLOBAL_FILTER_DATE_KEY = 'aggregationTimeRange';
const GLOBAL_FILTER_SEGMENT_KEY = 'aggregationSegmentId';

const globalFilters = {
    namespaced: true,
    state: {
        timeSeries: {},
        selectedSegmentId: undefined,
        selectedAccount: undefined,
        selectedApp: undefined,
        selectedApps: undefined,
        selectedChannel: undefined,
        selectedLanguage: undefined, // default value is undefined so that we have a way prevent aggs from being sent off if value has not been defined until languageFilter renders
        selectedVerification: undefined,
        starredMap: {},
        allTime: false,
        selectedFeatureType: undefined,
    },
    mutations: {
        setTimeSeries (state, { timeSeries }) {
            const dateRange = new VanillaDateRange(timeSeries.config);
            timeSeries.label = dateRange.toString();
            timeSeries.spec = dateRange.timeSeries();
            state.timeSeries = timeSeries;
        },
        setSelectedSegmentId (state, { selectedSegmentId, updateAngular }) {
            state.selectedSegmentId = selectedSegmentId;

            if (updateAngular) {
                if (selectedSegmentId) {
                    return PerSubscriptionUserStorage.set(GLOBAL_FILTER_SEGMENT_KEY, selectedSegmentId);
                }

                return PerSubscriptionUserStorage.remove(GLOBAL_FILTER_SEGMENT_KEY);
            }
        },
        setSelectedChannel (state, { selectedChannel = getDefaultSelectedChannel() } = {}) {
            state.selectedChannel = selectedChannel;
        },
        setSelectedApp (state, { selectedApp = getDefaultSelectedApp(), updateAngular }) {
            state.selectedApp = selectedApp;

            if (updateAngular) {
                return PerSubscriptionUserStorage.set(APP_STORAGE_KEY, selectedApp);
            }
        },
        setSelectedApps (state, { selectedApps = [getDefaultSelectedApp()], updateAngular }) {
            state.selectedApps = selectedApps;

            if (updateAngular) {
                // only save app id to user settings
                const selectedAppsIds = selectedApps.map((app) => {
                    return { id: app.id };
                });

                return PerSubscriptionUserStorage.set(APPS_STORAGE_KEY, selectedAppsIds);
            }
        },
        setSelectedAccount (state, { selectedAccount = defaultSelectedAccount, updateAngular }) {
            state.selectedAccount = selectedAccount;
            if (updateAngular) {
                return PerSubscriptionUserStorage.set(ACCOUNT_STORAGE_KEY, selectedAccount.account);
            }
        },
        setSelectedLanguage (state, { selectedLanguage = null }) {
            state.selectedLanguage = selectedLanguage;
            // Not using updateAngular pattern from previous examples,
            // language-filter.vue directly calls PerSubscriptionUserStorage
        },
        setVerification (state, { verification = null, persist }) {
            state.selectedVerification = verification;

            if (persist) {
                PerSubscriptionUserStorage.set(VERIFICATION_KEY, verification);
            }
        },
        setStarred (state, { starred = false, filterType, updateAngular }) {
            if (!filterType) {
                warn('The global star filter is deprecated. Please specify a unique filterType identifier.');
                filterType = 'default';
            }

            Vue.set(state.starredMap, filterType, starred);
            if (updateAngular) {
                return PerSubscriptionUserStorage.set('starsFilterMap', state.starredMap);
            }
        },
        setStarredMap (state, { map = {} }) {
            state.starredMap = map;
        },
        setAllTime (state, { allTime }) {
            state.allTime = allTime;

            return PerSubscriptionUserStorage.set(ALL_TIME_STORAGE_KEY, allTime);
        },
        setSelectedFeatureType (state, { featureType }) {
            state.selectedFeatureType = featureType;

            return PerSubscriptionUserStorage.set(FEATURE_TYPE_KEY, featureType);
        },
    },
    actions: {
        loadInitialStateFromUserSettings ({ commit, dispatch, rootState }) {
            const segmentIdSetting = useUserSettingsStore().settingByName(GLOBAL_FILTER_SEGMENT_KEY, true);
            const timeSeriesSetting = useUserSettingsStore().settingByName(GLOBAL_FILTER_DATE_KEY, true);
            const selectedSegmentId = segmentIdSetting?.value || 'everyone';

            commit('setSelectedSegmentId', { selectedSegmentId });
            dispatch('loadSelectedAccount');
            dispatch('loadSelectedChannel');

            let globalFilterDate = timeSeriesSetting?.value;
            if (!timeSeriesSetting?.value) {
                if (Date.now() - rootState.subscription.active.firstEventTime < 23 * 60 * 60 * 1000) {
                    globalFilterDate = DEFAULT_DATE_NEW;
                } else {
                    globalFilterDate = DEFAULT_DATE;
                }
            }

            const datesFromUserSetting = formatDates(globalFilterDate);
            const timeSeries = createTimeSeriesFromDate(datesFromUserSetting, false, true);
            if (globalFilterDate.type === 'Custom') {
                timeSeries.config.type = 'Custom';
            }
            commit('setTimeSeries', { timeSeries });
        },
        updateSelectedTimeSeries ({ commit }, { timeSeries }) {
            commit('setTimeSeries', { timeSeries });
            useUserSettingsStore().update({ name: GLOBAL_FILTER_DATE_KEY, namespaced: true, value: timeSeries.config });
        },
        loadSelectedAccount ({ commit }) {
            return PerSubscriptionUserStorage.get(ACCOUNT_STORAGE_KEY).then(({ value }) => {
                const savedSelectedAccount = value;
                commit('setSelectedAccount', {
                    selectedAccount: {
                        account: savedSelectedAccount || defaultSelectedAccount,
                    },
                    updateAngular: false,
                });
            });
        },
        loadSelectedChannel ({ commit }) {
            return PerSubscriptionUserStorage.get(CHANNEL_STORAGE_KEY).then(({ value }) => {
                commit('setSelectedChannel', {
                    selectedChannel: value || getDefaultSelectedChannel(),
                    updateAngular: false,
                });
            });
        },
        async loadSelectedApp ({ commit, rootGetters, dispatch }) {
            let { value } = await PerSubscriptionUserStorage.get(APP_STORAGE_KEY);
            if (value && !AppsService.getAppGroupLabelByAggId(value.id)) {
                // if the saved app isn't a default option, we check that it still exists
                await dispatch('apps/loadAll', {}, { root: true });
                if (!rootGetters['apps/getAppById'](value.id)) {
                    value = null;
                }
            }
            commit('setSelectedApp', {
                selectedApp: value || getDefaultSelectedApp(),
                updateAngular: false,
            });
        },
        async loadSelectedApps ({ state, commit, rootGetters, dispatch }) {
            let { selectedApps } = state;
            if (selectedApps) {
                return;
            }

            await dispatch('apps/loadAll', {}, { root: true });

            const { value: apps } = await PerSubscriptionUserStorage.get(APPS_STORAGE_KEY);
            const validApps =
                apps?.filter(({ id }) => APP_PLATFORM_LIST.includes(id) || rootGetters['apps/getAppById'](id)) || [];

            if (!validApps.length) {
                commit('setSelectedApps', {
                    selectedApps: [getDefaultSelectedApp()],
                    updateAngular: false,
                });

                return;
            }

            const platformOption = AppsService.getAppGroupLabelByAggId(validApps[0]?.id);
            selectedApps = validApps.map(({ id }) => {
                if (platformOption) {
                    return { id, displayName: platformOption };
                }

                return rootGetters['apps/getAppById'](id);
            });

            commit('setSelectedApps', {
                selectedApps,
                updateAngular: false,
            });
        },
        loadSelectedStarred ({ commit }) {
            return PerSubscriptionUserStorage.get('starsFilterMap').then(({ value: map }) => {
                // PSUS returns the literal Vuex state value, which for complex objects is pass-by-reference.
                // We want a separate copy of the map, so that our changes don't cause a reactive cascade.
                map = cloneDeep(map) || {};
                commit('setStarredMap', { map });
            });
        },
        loadSelectedLanguage ({ commit }) {
            return PerSubscriptionUserStorage.get('selectedLanguage').then(({ value: selectedLanguage }) => {
                commit('setSelectedLanguage', { selectedLanguage });
            });
        },
        async loadVerification ({ commit }) {
            const { value } = await PerSubscriptionUserStorage.get(VERIFICATION_KEY);
            commit('setVerification', {
                verification: value || 'all',
            });
        },
        async loadAllTime ({ commit }) {
            let { value } = await PerSubscriptionUserStorage.get(ALL_TIME_STORAGE_KEY);
            if (value === undefined || value === null) {
                value = true;
            }
            commit('setAllTime', { allTime: value });
        },
        resetSelectedApps ({ commit }) {
            commit('setSelectedApps', {
                selectedApps: undefined,
                updateAngular: true,
            });
        },
        async loadSelectedFeatureType ({ commit }) {
            const { value } = await PerSubscriptionUserStorage.get(FEATURE_TYPE_KEY);
            commit('setSelectedFeatureType', {
                featureType: value || defaultSelectedFeatureType,
            });
        },
    },
    getters: {
        selectedApp: (state) => state.selectedApps,
        timeSeries: (state) => state.timeSeries,
        selectedAccount: (state) => state.selectedAccount,
        selectedChannel: (state) => state.selectedChannel,
        selectedLanguage: (state) => state.selectedLanguage,
        selectedSegment: (state, getters, rootState, rootGetters) => {
            return rootGetters['segments/segmentById'](state.selectedSegmentId);
        },
        segmentBlacklist: (state) => {
            const segmentId = state.selectedSegmentId;

            return parseSegmentParams({ segmentId }).blacklist || 'apply';
        },
        segmentIncludeAnonymous: (state) => {
            const segmentId = state.selectedSegmentId;

            return parseSegmentParams({ segmentId }).includeAnonymous;
        },
        segmentIncludesDNT: (state) => {
            const segmentId = state.selectedSegmentId;

            return parseSegmentParams({ segmentId }).includeDNT;
        },
        segmentId: (state) => {
            const segmentId = state.selectedSegmentId;

            return parseSegmentParams({ segmentId }).segmentId;
        },
        selectedSegmentId: (state) => {
            return state.selectedSegmentId;
        },
        timeSeriesSpec: (state) => {
            return state.timeSeries.spec;
        },
        selectedAccountAccount: (state) => {
            return get(state.selectedAccount, 'account');
        },
        appId: (state) => {
            if (!state.selectedApps) return;

            const appIds = state.selectedApps.map((app) => AppChooserService.getAppIds(app));

            // "all mobile apps" turns into ['expandAppIds("IOS")', 'expandAppIds("android")']
            // we need to make sure to flatten that down to a single array
            return flatten(appIds);
        },
        nonAggAppId: (state, getters) => {
            const { appId } = getters;

            if (!appId || (Array.isArray(appId) && appId.length === 0)) return ['*'];

            // for backwards compat, appId is only an array when it contains a distinct selection of app ids
            // a single app selection, as well as selection shorthand like expandAppId('*') are not arrays
            if (isArray(appId)) {
                return appId.map(transformAggAppIdIntoBareAppId);
            }

            return transformAggAppIdIntoBareAppId(appId);
        },
        starredByType: (state) => (type) => {
            return !!(type && state.starredMap[type]);
        },
        selectedAppEventRate: (state, getters) => {
            const apps = getters.selectedApp;
            if (!apps?.length) return 1;

            const eventRates = apps.map((app) => app.eventRate);
            const maxRate = Math.max.apply(null, eventRates);

            return maxRate || 1;
        },
        selectedAppLatestEventTime: (state, getters) => {
            const apps = getters.selectedApp;
            if (!apps?.length) return -1;

            const eventTime = apps.map((app) => app.latestEventTime);
            const maxTime = Math.max.apply(null, eventTime);

            return maxTime || -1;
        },
        allTime: (state) => {
            return state.allTime;
        },
    },
};

export default globalFilters;
